import React, {useCallback, useEffect, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import {ComboBox, Label, FlexColumn, FlexRow, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import environment from '../../../../../RelayEnvironment';
import {useInfiniteScroll} from '../../../hooks/useInfiniteScroll';

const ClientPrimaryContactDropdownQuery = graphql`
	query ClientPrimaryContactDropdownQuery($clientId: String, $first: Int, $after: String) {
		viewer {
			company {
				client(id: $clientId) {
					primaryContactId
					clientContacts(first: $first, after: $after) {
						pageInfo {
							endCursor
							hasNextPage
						}
						edges {
							node {
								id
								firstName
								lastName
							}
						}
					}
				}
			}
		}
	}
`;

const PAGE_SIZE = 20;

// Sentinel id for the non-selectable "no contacts" row shown when the selected client has no
// contacts. Lets the menu open (the ComboBox would otherwise not expand on an empty collection)
// and surfaces an empty-state message.
const EMPTY_STATE_KEY = '__no_contacts__';

interface Contact {
	id: string;
	name: string;
}

interface ClientContactNode {
	id: string;
	firstName: string | null;
	lastName: string | null;
}

interface ClientContactsQueryResponse {
	viewer?: {
		company?: {
			client?: {
				primaryContactId?: string | null;
				clientContacts?: {
					pageInfo: {endCursor: string | null; hasNextPage: boolean};
					edges: Array<{node: ClientContactNode}>;
				};
			};
		};
	};
}

interface ClientPrimaryContactDropdownProps {
	clientId: string;
	value: Contact | null;
	onChange: (contact: Contact | null) => void;
	label?: string;
	// When the dropdown is embedded in a layout that already renders its own label
	// (e.g. the project settings InputLine rows), hide the built-in label.
	hideLabel?: boolean;
	placeholder?: string;
	// Forwarded to the underlying ComboBox as data-cy so the picker can be targeted in tests.
	dataCy?: string;
	// Renders a display-only "Primary" pill next to the client's primary contact.
	showPrimaryChip?: boolean;
	// Fired once when the first page resolves with no contacts. Lets a caller show its own
	// message alongside the picker's inline empty state.
	onEmpty?: () => void;
}

export const ClientPrimaryContactDropdown = ({
	clientId,
	value,
	onChange,
	label,
	hideLabel,
	placeholder,
	dataCy,
	showPrimaryChip = false,
	onEmpty,
}: ClientPrimaryContactDropdownProps) => {
	const {formatMessage} = useIntl();
	const [contacts, setContacts] = useState<Contact[]>(value ? [value] : []);
	const [primaryContactId, setPrimaryContactId] = useState<string | null>(null);
	const [cursor, setCursor] = useState<string | null>(null);
	const [hasNextPage, setHasNextPage] = useState(true);
	// Tracks whether the first page has resolved, so we don't flash the empty state while loading.
	const [loaded, setLoaded] = useState(false);
	const loadingRef = useRef(false);
	// Latches the one-shot onEmpty notification; reset whenever the client changes.
	const emptyNotified = useRef(false);

	const fetchPage = useCallback(
		(after: string | null) => {
			if (loadingRef.current) return;
			loadingRef.current = true;
			fetchQuery(environment.getInstance(), ClientPrimaryContactDropdownQuery, {
				clientId,
				first: PAGE_SIZE,
				after,
			})
				.toPromise()
				.then(
					(data: ClientContactsQueryResponse | undefined) => {
						// The primary contact is a property of the client, so only the first page carries it.
						if (!after) {
							setPrimaryContactId(data?.viewer?.company?.client?.primaryContactId ?? null);
						}
						const conn = data?.viewer?.company?.client?.clientContacts;
						if (!conn) {
							// No connection is not an answer about how many contacts exist, so `loaded`
							// stays false and the empty state is never claimed on this path.
							loadingRef.current = false;
							return;
						}
						const nodes: Contact[] = conn.edges.map(e => ({
							id: e.node.id,
							name: [e.node.firstName, e.node.lastName].filter(Boolean).join(' '),
						}));
						setContacts(prev => {
							if (after) return [...prev, ...nodes];
							// Preserve any pre-loaded contacts (e.g. the currently-selected primary
							// contact) that aren't returned in the first page of results.
							const nodeIds = new Set(nodes.map(n => n.id));
							const preserved = prev.filter(c => !nodeIds.has(c.id));
							return [...preserved, ...nodes];
						});
						setCursor(conn.pageInfo.endCursor);
						setHasNextPage(conn.pageInfo.hasNextPage);
						setLoaded(true);
						loadingRef.current = false;
					},
					() => {
						// A failed request says nothing about whether this client has contacts. Leaving
						// `loaded` false keeps the picker quiet instead of asserting there are none -
						// which a caller latches into a blocking "add a contact first" message.
						loadingRef.current = false;
					}
				);
		},
		[clientId]
	);

	useEffect(() => {
		setLoaded(false);
		setPrimaryContactId(null);
		emptyNotified.current = false;
		fetchPage(null);
	}, [clientId]);

	const loadMore = useCallback(() => {
		if (!loadingRef.current && hasNextPage) {
			fetchPage(cursor);
		}
	}, [hasNextPage, cursor, fetchPage]);

	const {handleScroll} = useInfiniteScroll({hasNextPage, onLoadMore: loadMore});

	useEffect(() => {
		// The ComboBox renders its listbox inside a React Aria <Overlay> portal (appended to document.body),
		// so scroll events from the <ul role="listbox"> do not bubble to a wrapper div around <ComboBox>.
		// We use a MutationObserver to detect when the portal listbox appears/disappears and
		// attach a native scroll listener directly to it.
		let listboxEl: Element | null = null;

		const attachListener = (el: Element) => {
			listboxEl = el;
			el.addEventListener('scroll', handleScroll as unknown as EventListener);
		};

		const detachListener = () => {
			if (listboxEl) {
				listboxEl.removeEventListener('scroll', handleScroll as unknown as EventListener);
				listboxEl = null;
			}
		};

		const observer = new MutationObserver(() => {
			const found = document.querySelector('[role="listbox"]');
			if (found && found !== listboxEl) {
				detachListener();
				attachListener(found);
			} else if (!found && listboxEl) {
				detachListener();
			}
		});

		observer.observe(document.body, {childList: true, subtree: true});

		return () => {
			observer.disconnect();
			detachListener();
		};
	}, [handleScroll]);

	// The inline default is unreachable — 'clients.client-contact' exists in the catalog as
	// "Client Contact", and react-intl only falls back for ids that are missing entirely.
	const dropdownLabel = label ?? formatMessage({id: 'clients.client-contact'});

	const handleChange = (key: string) => {
		if (!key || key === EMPTY_STATE_KEY) {
			onChange(null);
			return;
		}
		const found = contacts.find(c => c.id === key) ?? null;
		onChange(found);
	};

	// When the selected client has no contacts (first page resolved, nothing returned), show a single
	// non-selectable row so the menu still opens and an empty-state message is visible. `loaded` is
	// now only true when the server actually answered, so a failed or empty-bodied response no longer
	// reads as "this client has no contacts".
	const isEmpty = loaded && contacts.length === 0;

	// Latched so the documented "fired once" contract holds even with onEmpty in the dep array, which
	// a caller passing an inline arrow would otherwise re-fire on every render.
	useEffect(() => {
		if (isEmpty && !emptyNotified.current) {
			emptyNotified.current = true;
			if (onEmpty) onEmpty();
		}
	}, [isEmpty, onEmpty]);

	const items: Contact[] = isEmpty
		? [{id: EMPTY_STATE_KEY, name: formatMessage({id: 'clients.client-contacts-empty-state'})}]
		: contacts;

	const comboBox = (
		<ComboBox
			defaultItems={items}
			value={value?.id ?? undefined}
			onChange={handleChange}
			width={'flex'}
			placeholder={placeholder}
			disabledKeys={[EMPTY_STATE_KEY]}
			data-cy={dataCy}
		>
			{(contact: Contact) => (
				<ComboBox.Item key={contact.id} textValue={contact.name}>
					{showPrimaryChip && contact.id === primaryContactId ? (
						<FlexRow gap="s" alignItems="center">
							<Text>{contact.name}</Text>
							<Pill variant={PillVariant.NEUTRAL}>{formatMessage({id: 'contacts_panel.badge_primary'})}</Pill>
						</FlexRow>
					) : (
						contact.name
					)}
				</ComboBox.Item>
			)}
		</ComboBox>
	);

	if (hideLabel) {
		return comboBox;
	}

	return (
		<FlexColumn gap={'s'}>
			<Label>{dropdownLabel}</Label>
			{comboBox}
		</FlexColumn>
	);
};
